import { LANDING_HOST } from './landing-intent';

/**
 * A visitor who came from an affiliate link (Impact tags the landing page with `utm_campaign=af_<id>`)
 * is sent to the plan picker after sign-in, and to the affiliate's own landing page once they hold a
 * paid plan. The affiliate is only visible on the first page load — the landing page's `?referrer=`
 * survives the sign-in bounce, then `/welcome` replaces its URL within seconds — so it is kept in
 * `localStorage` until the purchase.
 *
 * A copy is also kept on the visitor's own recipient (`lib/affiliate-arrival-server.ts`) once they have one,
 * because a buyer who pays in another browser, profile or private window has no `localStorage` copy. After a
 * confirmed payment the browser copy is read first, and the account copy only when the browser has none.
 *
 * The landing URL is never read from the link or stored: the server answers it from config
 * (`/api/affiliate/landing`, `/api/affiliate/claim`), so a crafted value cannot send a paying user to an
 * arbitrary site.
 */

export const AFFILIATE_ARRIVAL_STORAGE_KEY = 'symphony-affiliate-arrival';

/** Long enough to sign up and buy later this week; short enough that a shared machine forgets. */
export const AFFILIATE_ARRIVAL_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const AFFILIATE_CAMPAIGN = /^af_\d{1,12}$/;

export interface AffiliateArrival {
  /** The Impact campaign tag, e.g. `af_2982618`. */
  partner: string;
  /** Who this was captured for. '' when the caller could not be identified at capture time. */
  userId: string;
  capturedAt: number;
}

/** The affiliate a landing page `referrer` names, or null when it is not an affiliate visit to our landing site. */
export function affiliatePartnerFromReferrer(raw: unknown): string | null {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  if (!LANDING_HOST.test(url.hostname)) return null;
  const campaign = url.searchParams.get('utm_campaign') ?? '';
  return AFFILIATE_CAMPAIGN.test(campaign) ? campaign : null;
}

/**
 * The arrival to act on, or null. Rejects an expired record and one captured for a different signed-in
 * user; a record with no userId stays usable, the same rule `landing-intent.ts` uses.
 */
export function affiliateArrivalToUse({
  stored,
  userId,
  now,
}: {
  stored: AffiliateArrival | null;
  userId: string;
  now: number;
}): AffiliateArrival | null {
  if (!stored) return null;
  if (now - stored.capturedAt > AFFILIATE_ARRIVAL_TTL_MS) return null;
  if (stored.userId && userId && stored.userId !== userId) return null;
  return stored;
}

/** Parses whatever is in storage, tolerating anything a previous version or a user may have left. */
export function parseStoredAffiliateArrival(rawJson: string | null): AffiliateArrival | null {
  if (!rawJson) return null;
  try {
    const value = JSON.parse(rawJson) as Partial<AffiliateArrival>;
    if (typeof value?.partner !== 'string' || !AFFILIATE_CAMPAIGN.test(value.partner)) return null;
    if (typeof value.capturedAt !== 'number') return null;
    return {
      partner: value.partner,
      userId: typeof value.userId === 'string' ? value.userId : '',
      capturedAt: value.capturedAt,
    };
  } catch {
    return null;
  }
}

// ---- the account copy, `<partner>|<capturedAtMs>[|spent]` under `Recipient.metadata.values.affiliateArrival`.
// Pure, so it is unit-tested without the RPC layer; `lib/affiliate-arrival-server.ts` reads and writes it.
//
// A spent arrival stays as a marker rather than being deleted: another browser may still hold the same
// arrival in `localStorage` and save it again, and only a newer capture may replace what is stored.

export const ACCOUNT_AFFILIATE_ARRIVAL_KEY = 'affiliateArrival';

export interface AccountAffiliateArrival {
  partner: string;
  capturedAt: number;
  spent: boolean;
}

export function encodeAccountAffiliateArrival({ partner, capturedAt, spent }: AccountAffiliateArrival): string {
  return `${partner}|${Math.floor(capturedAt)}${spent ? '|spent' : ''}`;
}

/** `null` for absent or malformed: a value that cannot be read is never acted on. */
export function parseAccountAffiliateArrival(raw: string | undefined | null): AccountAffiliateArrival | null {
  if (!raw) return null;
  const [partner, capturedAtRaw, flag, ...rest] = raw.split('|');
  if (rest.length > 0 || (flag !== undefined && flag !== 'spent')) return null;
  if (!partner || !AFFILIATE_CAMPAIGN.test(partner)) return null;
  const capturedAt = Number(capturedAtRaw);
  if (!Number.isFinite(capturedAt) || capturedAt <= 0) return null;
  return { partner, capturedAt, spent: flag === 'spent' };
}

export function isAffiliateArrivalFresh(capturedAt: number, now: number): boolean {
  return now - capturedAt <= AFFILIATE_ARRIVAL_TTL_MS;
}

// ---- storage. Every path is guarded: Safari private mode throws on write, and an affiliate redirect is
// never worth breaking a signup or a purchase over.

export function saveAffiliateArrival(record: AffiliateArrival): void {
  try {
    window.localStorage.setItem(AFFILIATE_ARRIVAL_STORAGE_KEY, JSON.stringify(record));
  } catch (err) {
    console.warn('[affiliate-arrival] could not save the arrival; this visitor will not be redirected', err);
  }
}

export function loadAffiliateArrival(userId: string): AffiliateArrival | null {
  try {
    return affiliateArrivalToUse({
      stored: parseStoredAffiliateArrival(window.localStorage.getItem(AFFILIATE_ARRIVAL_STORAGE_KEY)),
      userId,
      now: Date.now(),
    });
  } catch {
    return null;
  }
}

export function clearAffiliateArrival(): void {
  try {
    window.localStorage.removeItem(AFFILIATE_ARRIVAL_STORAGE_KEY);
  } catch (err) {
    console.warn('[affiliate-arrival] could not clear the arrival', err);
  }
}
